<script lang="ts" setup>
import type { InputDateProps } from '@nuxt/ui';
import type { Temporal } from 'temporal-polyfill';
import { reactivePick } from '@vueuse/core';
import { tv } from 'tailwind-variants';
import { computed } from 'vue';
import nuxtUiInputTheme from '#build/ui/input';
import DButton from '#layers/ui/components/Button.vue';
import DTooltip from '#layers/ui/components/Tooltip.vue';
import { useMergedTheme } from '#layers/ui/composables/use-merged-theme';
import { useReadonly } from '#layers/ui/composables/use-readonly';
import DateRangePickerBase from './DateRangePickerBase.vue';

interface Props extends Pick<InputDateProps, 'color' | 'variant' | 'size' | 'disabled'> {
	readonly?: boolean;
	block?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	block: true,
});

const startDate = defineModel<Temporal.PlainDate | null>('startDate');
const endDate = defineModel<Temporal.PlainDate | null>('endDate');

const pickerProps = reactivePick(props, 'color', 'variant', 'size', 'disabled', 'readonly', 'block');

const inputTheme = useMergedTheme({ input: nuxtUiInputTheme });
const theme = computed(() => tv(inputTheme)({
	color: props.color,
	variant: props.variant,
	size: props.size,
	leading: true,
	trailing: true,
}));

const { preventKeyboardNavigation, preventPointer } = useReadonly(() => props.readonly);
</script>

<template>
	<DateRangePickerBase
		v-bind="pickerProps"
		v-model:start-date="startDate"
		v-model:end-date="endDate"
	>
		<template #trigger="{ rangeFieldDisplay, isRangeEmpty, canClearRange, clearRange }">
			<button
				type="button"
				data-test-id="date-range-trigger"
				:disabled
				:aria-readonly="readonly"
				:class="theme.root({
					class: [
						block ? 'w-full' : '',
						disabled ? 'opacity-50' : '',
						disabled ? 'cursor-not-allowed' : props.readonly ? 'cursor-default' : 'cursor-pointer',
					],
				})"
				@pointerdown.capture="preventPointer"
				@click.capture="preventPointer"
				@keydown.capture="preventKeyboardNavigation"
			>
				<span :class="theme.leading()">
					<DIcon name="lucide:calendar" :class="theme.leadingIcon()" />
				</span>

				<span
					:class="theme.base({
						class: [
							'w-full truncate text-start select-none',
							isRangeEmpty ? 'text-dimmed' : 'text-default',
							disabled ? 'cursor-not-allowed' : props.readonly ? 'cursor-default' : 'cursor-pointer',
						],
					})"
				>
					{{ rangeFieldDisplay }}
				</span>

				<span :class="theme.trailing()">
					<DTooltip v-if="canClearRange" :text="$t('common.clear')">
						<DButton
							type="button"
							color="neutral"
							variant="link"
							icon="lucide:x"
							size="sm"
							data-test-id="date-range-clear-range"
							class="p-0 text-dimmed"
							:disabled
							@click.stop="clearRange"
							@pointerdown.stop
						/>
					</DTooltip>
					<DIcon v-else-if="!readonly" name="lucide:chevron-down" :class="theme.trailingIcon()" />
				</span>
			</button>
		</template>
	</DateRangePickerBase>
</template>
